import { useState, useEffect, useRef } from "react"
import { PlanSummary } from "./Step1BillingForm"
import { Shield, QrCode, CreditCard, RefreshCw } from "lucide-react"
import { generatePromptPayQR } from "@/api/kuizy"



function Countdown({ seconds, onExpire }) {
  const [left, setLeft] = useState(seconds)
  const timer = useRef(null)

  useEffect(() => {
    timer.current = setInterval(() => {
      setLeft(p => {
        if (p <= 1) { clearInterval(timer.current); onExpire?.(); return 0 }
        return p - 1
      })
    }, 1000)
    return () => clearInterval(timer.current)
  }, [])

  const m = String(Math.floor(left / 60)).padStart(2, "0")
  const s = String(left % 60).padStart(2, "0")
  return (
    <span className={`font-mono font-semibold ${left < 60 ? "text-red-500" : "text-orange-500"}`}>
      {m}:{s}
    </span>
  )
}

export default function Step2PaymentForm({ plan, billing, payment, onChange, onNext, onBack }) {
  const vat = Math.round((plan.price_monthly || 0) * 0.07)
  const total = (plan.price_monthly || 0) + vat
  const [expired, setExpired] = useState(false)
  const [qrBase64, setQrBase64] = useState(null)
  const [qrLoading, setQrLoading] = useState(false)

  useEffect(() => {
    if (payment.method === "qr" && total > 0 && !qrBase64 && !qrLoading && !expired) {
      let active = true;
      setQrLoading(true);
      const orgId = localStorage.getItem("orgId") || "0000";
      generatePromptPayQR({
        biller_id: "010555612629101", // Default PromptPay ID
        ref1: 'INV202603001',
        ref2: 'CUST998877',
        ref3: "0000000000",
        amount: total
      }).then(res => {
        console.log(res);
        if (active) {
          setQrBase64(res?.qr_base64);
          console.log(res?.qr_base64);

          setQrLoading(false);
          setExpired(false);
        }
      }).catch(err => {
        if (active) {
          console.error("QR Generate Error:", err);
          setQrLoading(false);
        }
      });
      return () => { active = false; };
    }
  }, [payment.method, total, plan, billing, qrBase64, qrLoading, expired]);

  const set = (k, v) => onChange({ ...payment, [k]: v })

  const cardInput = "border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 w-full bg-white"
  const lbl = "block text-sm font-medium text-gray-700 mb-1"

  const canSubmit = payment.method === "qr"
    ? !expired
    : payment.card_name && payment.card_number && payment.exp_month && payment.exp_year?.length === 4 && payment.cvv

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
      {/* Left */}
      <div className="flex-1 w-full min-w-0">
        <h2 className="text-xl font-bold mb-1">เลือกวิธีชำระเงิน</h2>
        <p className="text-sm text-gray-500 mb-5">
          เลือกช่องทางที่สะดวก ระบบปลอดภัยด้วย SSL 256-bit encryption
        </p>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm font-semibold mb-4">วิธีการชำระเงิน</p>

          {/* Method selector */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            {[
              { key: "qr", icon: <QrCode className="text-blue-500" size={34} />, title: "QR Code", sub: "PromptPay / Mobile Banking", bg: "bg-blue-200" },
              { key: "card", icon: <CreditCard className="text-white" size={34} />, title: "บัตรเครดิต / เดบิต", sub: "Visa, Mastercard, JCB", bg: "bg-green-600" },
            ].map(({ key, icon, title, sub, bg }) => (
              <button
                key={key}
                type="button"
                onClick={() => set("method", key)}
                className={`relative border-2 rounded-xl p-5 text-center transition cursor-pointer
                  ${payment.method === key
                    ? "border-pink-500 bg-pink-50"
                    : "border-gray-200 bg-white hover:border-pink-300"
                  }`}
              >
                {/* radio */}
                <div className={`absolute top-3 right-3 w-4 h-4 rounded-full border-2
                  ${payment.method === key ? "border-pink-500 bg-pink-500" : "border-gray-200 bg-white"}`}>
                  {payment.method === key && (
                    <span className="absolute inset-0 flex items-center justify-center text-white text-[8px]">✓</span>
                  )}
                </div>

                {/* icon center */}
                <div className="flex justify-center mb-3">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${bg}`}>
                    {icon}
                  </div>
                </div>

                <p className="font-semibold text-sm">{title}</p>
                <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
              </button>
            ))}
          </div>

          {/* QR Content */}
          {payment.method === "qr" && (
            <div className="text-center py-4">
              <div className="mx-auto border-2 border-gray-300 rounded-lg flex items-center justify-center bg-white 
                w-40 h-40 
                sm:w-48 sm:h-48 
                md:w-56 md:h-56 
                lg:w-64 lg:h-64 relative overflow-hidden">
                {qrLoading ? (
                  <div className="flex flex-col items-center justify-center text-gray-400">
                    <RefreshCw className="w-8 h-8 mb-2 animate-spin text-pink-500" />
                    <span className="text-sm">กำลังสร้าง QR...</span>
                  </div>
                ) : qrBase64 ? (
                  <img
                    src={qrBase64.startsWith("data:image") ? qrBase64 : `data:image/png;base64,${qrBase64}`}
                    alt="PromptPay QR Code"
                    className="w-full h-full object-contain p-2"
                  />
                ) : (
                  <div className="text-gray-400 text-sm">ไม่สามารถสร้าง QR ได้</div>
                )}

                {expired && (
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center z-10 transition-all duration-300">
                    <p className="text-red-600 font-bold mb-3 text-lg drop-shadow-sm">หมดเวลาชำระเงิน</p>
                    <button
                      onClick={() => { setExpired(false); setQrBase64(null); }}
                      className="px-5 py-2 bg-pink-500 text-white rounded-full text-sm font-semibold hover:bg-pink-600 hover:shadow-lg transition-all"
                    >
                      สร้าง QR ใหม่
                    </button>
                  </div>
                )}
              </div>
              <p className="text-2xl font-bold text-pink-500 mt-4">
                ฿{total.toLocaleString()}.00
              </p>
              <p className="text-sm text-gray-500 mt-1">
                สแกน QR Code ด้วยแอปธนาคาร หรือผ่าน PromptPay
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                เลือก 'ยืนยันการชำระเงิน' หลังโอนเงินเรียบร้อย
              </p>
              <div className="mt-3 inline-flex items-center gap-2 bg-orange-50 text-orange-600 border border-orange-200 rounded-full px-4 py-1.5 text-sm">
                <span>⏱</span>
                {expired
                  ? <span className="text-red-600 font-semibold">QR หมดอายุแล้ว</span>
                  : <><span>QR หมดอายุใน</span> <Countdown seconds={300} onExpire={() => setExpired(true)} /></>
                }
              </div>
              <p className="text-xs text-gray-400 text-center mt-2 flex items-center justify-center gap-1 mt-6">
                <Shield className="text-green-500" />
                ข้อมูลของคุณถูกส่งผ่านระบบ SSL 256-bit ปลอดภัย 100%
              </p>
            </div>
          )}

          {/* Card Content */}
          {payment.method === "card" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>ชื่อบนบัตร <span className="text-pink-500">*</span></label>
                  <input
                    value={payment.card_name || ""}
                    onChange={e => set("card_name", e.target.value.toUpperCase())}
                    placeholder="FRISTNAME LASTNAME"
                    className={cardInput}
                  />
                </div>
                <div className="relative">
                  <label className={lbl}>หมายเลขบัตร <span className="text-pink-500">*</span></label>
                  <input
                    value={payment.card_number || ""}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, "").slice(0, 16)
                      set("card_number", v.replace(/(.{4})/g, "$1 ").trim())
                    }}
                    placeholder="0000 0000 0000 0000"
                    className={cardInput + " pr-14"}
                    maxLength={19}
                  />
                  <span className="absolute right-3 bottom-2.5 text-sm font-bold text-blue-700 tracking-wide">VISA</span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>วันหมดอายุ <span className="text-pink-500">*</span></label>
                  <input
                    value={payment.exp_date || ""}
                    onChange={e => {
                      let v = e.target.value;
                      if (payment.exp_date?.endsWith("/") && v.length === payment.exp_date.length - 1) {
                        v = v.slice(0, v.length - 1);
                      }
                      let digits = v.replace(/\D/g, "");
                      let m = digits.slice(0, 2);
                      let y = digits.slice(2, 6);
                      if (m.length === 2) {
                        let mInt = parseInt(m, 10);
                        if (mInt > 12) m = "12";
                        if (mInt === 0) m = "01";
                      }
                      let display = m;
                      if (digits.length > 2) {
                        display = m + "/" + y;
                      } else if (v.endsWith("/")) {
                        if (m.length === 1 && m !== "0") m = "0" + m;
                        display = m + "/";
                      }
                      onChange({ ...payment, exp_date: display, exp_month: m, exp_year: y });
                    }}
                    placeholder="MM / YYYY"
                    className={cardInput}
                    maxLength={7}
                  />
                </div>
                <div>
                  <label className={lbl}>CVV <span className="text-pink-500">*</span></label>
                  <input
                    value={payment.cvv || ""}
                    onChange={e => set("cvv", e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="···"
                    type="password"
                    className={cardInput}
                    maxLength={4}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 text-center mt-2 flex items-center justify-center gap-1 mt-6">
                <Shield className="text-green-500" />
                ข้อมูลของคุณถูกส่งผ่านระบบ SSL 256-bit ปลอดภัย 100%
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Right: Summary */}
      <PlanSummary plan={plan} vat={vat} total={total} billing={billing} step={2} />
    </div>
  )
}
